import * as Sentry from "@sentry/react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { Text } from "src/sudokit/components/Text";
import { Dialog, DialogContent, DialogHeader } from "../../sudokit/components/Dialog";
import { useConvertProtoProject, useGetProtoProject } from "./PrefilledProjectQueries";

interface PrefilledProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shortCode: string;
  projectTitle: string;
  onProjectCreated?: () => void;
  onCancel?: () => void;
}

const loadingQuotes = [
  { text: "Summoning your muse...", author: "She's notoriously late" },
  { text: "Brewing literary magic...", author: "Double, double, toil and trouble" },
  { text: "Arranging plot twists...", author: "No one expects them" },
  { text: "Polishing prose...", author: "Until it gleams" },
  { text: "Channeling Hemingway...", author: "But with better adjectives" },
  { text: "Consulting the writing gods...", author: "They're debating" },
  { text: "Weaving narrative threads...", author: "Into something magnificent" },
  { text: "Opening creative portals...", author: "Stand back" },
  { text: "Dusting off ancient manuscripts...", author: "Handle with care" },
  { text: "Aligning literary stars...", author: "Mercury is in retrograde" },
];

export default function PrefilledProjectDialog({ open, onOpenChange, shortCode, projectTitle, onProjectCreated, onCancel }: PrefilledProjectDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const history = useHistory();

  const getProtoProject = useGetProtoProject();
  const convertProtoProject = useConvertProtoProject();

  useEffect(() => {
    if (!loading) return;

    const quoteInterval = setInterval(() => {
      setCurrentQuoteIndex((prev) => (prev + 1) % loadingQuotes.length);
    }, 5000);

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 15;
      });
    }, 2000);

    return () => {
      clearInterval(quoteInterval);
      clearInterval(progressInterval);
    };
  }, [loading]);

  const handleCreateProject = async () => {
    setLoading(true);
    setError(null);
    setProgress(0);
    setCurrentQuoteIndex(0);

    try {
      const protoProject = await getProtoProject.mutateAsync(shortCode);

      if (!protoProject) {
        setError("This project link has expired or is no longer available.");
        return;
      }

      const result = await convertProtoProject.mutateAsync(shortCode);

      if (result.success && result.projectId) {
        setProgress(100);
        const url = new URL(window.location.href);
        url.searchParams.delete("prefilled");
        window.history.replaceState({}, document.title, url.pathname + url.search);
        window.sessionStorage.removeItem("prefilled_project_code");

        if (onProjectCreated) {
          onProjectCreated();
        }

        setTimeout(() => {
          history.push(`/app/project/${result.projectId}`);
          onOpenChange(false);
        }, 500);
      } else {
        Sentry.captureException(result.error);
        setError(result.error || "Failed to create project. Please try again.");
      }
    } catch (err) {
      Sentry.captureException(err);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("prefilled");
    window.history.replaceState({}, document.title, url.pathname + url.search);
    window.sessionStorage.removeItem("prefilled_project_code");

    // Notify parent component that user cancelled
    if (onCancel) {
      onCancel();
    }

    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={loading ? () => {} : onOpenChange}
    >
      <DialogContent
        className="max-w-lg overflow-hidden sm:rounded-2xl bg-app-menu-color"
        hideClose={loading}
      >
        {!loading ? (
          <motion.div
            key="initial"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <DialogHeader>
              <Text
                as="h2"
                size="2xl"
                font="gt-super-display"
                letterSpacing="tight"
                color="grey-0"
                weight="medium"
              >
                A Literary Gift Awaits
              </Text>
              <div className="mt-3">
                <Text
                  size="base"
                  color="grey-1"
                  lineHeight="relaxed"
                  className="mb-2"
                >
                  You've been invited to begin a new story:
                </Text>
                <Text
                  size="lg"
                  font="gt-super-text"
                  color="grey-0"
                  className="italic"
                >
                  "{projectTitle}"
                </Text>
              </div>
            </DialogHeader>

            <div className="mt-8 space-y-4">
              {error && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-red-50 p-4 rounded-lg border border-red-200"
                >
                  <Text
                    size="sm"
                    color="danger-color"
                  >
                    {error}
                  </Text>
                </motion.div>
              )}

              <div className="flex gap-3 justify-end">
                <SudoButton
                  onClick={handleCancel}
                  disabled={loading}
                  variant="tertiaryGrey"
                  className="min-w-[100px]"
                >
                  Not Now
                </SudoButton>
                <SudoButton
                  onClick={handleCreateProject}
                  disabled={loading}
                  variant="primary"
                  className="min-w-[140px]"
                >
                  Accept & Create
                </SudoButton>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="pb-8"
          >
            <div className="text-center space-y-6">
              <div className="space-y-4">
                <div className="h-20 flex flex-col justify-center px-8">
                  <motion.div
                    key={currentQuoteIndex}
                    initial={{ opacity: 0, translateY: 10 }}
                    animate={{ opacity: 1, translateY: 0 }}
                    exit={{ opacity: 0, translateY: -10 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-0"
                  >
                    <Text
                      size="lg"
                      font="gt-super-text"
                      color="grey-0"
                      align="center"
                      weight="medium"
                    >
                      {loadingQuotes[currentQuoteIndex].text}
                    </Text>
                    <Text
                      size="sm"
                      color="grey-1"
                      align="center"
                      className="italic"
                    >
                      {loadingQuotes[currentQuoteIndex].author}
                    </Text>
                  </motion.div>
                </div>

                <div className="px-12">
                  <div className="h-2 bg-app-grey-3 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full rounded-full scan-horizontal"
                      initial={{ width: "0%" }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                    />
                  </div>
                  <div className="h-5 flex items-center justify-center mt-2">
                    <Text
                      size="xs"
                      color="grey-1"
                      align="center"
                    >
                      {progress < 30 && "Gathering inspiration..."}
                      {progress >= 30 && progress < 60 && "Building your creative space..."}
                      {progress >= 60 && progress < 90 && "Almost there..."}
                      {progress >= 90 && "Finalizing your masterpiece..."}
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </DialogContent>
    </Dialog>
  );
}
